import ButtonCollection from "@components/ButtonCollection/ButtonCollection";
import { JMCButton } from "@components/JMCButton/JMCButton";
import { JMCCardStyleWrapper } from "@components/JMCCardStyleWrapper/JMCCardStyleWrapper";
import { JMCHtml } from "@components/JMCHtml/JMCHtml";
import { CardFooter } from "@jmc/core/src/components/CardFooter";
import GatsbyImageCard, { GatsbyImageCardProps } from "@jmc/core/src/components/GatsbyImageCard/GatsbyImageCard";
import { Card } from "@jmc/solid-design-system/src/components/molecules/Card/Card";
import { useJnjBranding } from "@jmc/utils/hooks/useJnjBranding";
import { CMSButtonCollectionProps, CMSButtonProps, CMSFootnotes } from "@types";
import React from "react";

import style from "./style.module.scss";

interface Props {
    cardProps: GatsbyImageCardProps;
    footnotes: CMSFootnotes[];
    sectionIndex: number;
    placement: string;
}
export const JMCCard = ({ cardProps, footnotes, sectionIndex, placement }: Props): JSX.Element => {
    const { jnjFullBranded } = useJnjBranding();
    const textColor = jnjFullBranded ? "dark" : cardProps.fullBackground ? "white" : "dark";

    const buildButton = (button: CMSButtonProps, index: number): JSX.Element => {
        const buttonProps = {
            ...button,
        } as CMSButtonProps;

        if (jnjFullBranded) {
            buttonProps.color = "Primary 550";
            buttonProps.variant = "Naked";
            buttonProps.flexibleWidth = true;
        }
        const icon = jnjFullBranded ? <Card.CTAIconArrow /> : undefined;

        return (
            <div className={style.inlineBlock} key={button?.text?.substring(0, 10) + index}>
                <JMCButton button={buttonProps} placement={placement} footnotes={footnotes} accessoryRight={icon} />
            </div>
        );
    };

    const card = (
        <JMCCardStyleWrapper
            data-test-id={`Card.Color overlay - ${cardProps.id}`}
            backgroundColor={
                !jnjFullBranded
                    ? cardProps.fullBackground
                        ? "--background-transparent"
                        : cardProps.background_color
                    : cardProps.background_color
            }
            titleColor={cardProps.title_color}
            textColor={cardProps.text_color || textColor}
        >
            <GatsbyImageCard
                data-test-id={`Card.${cardProps.id}`}
                title={<JMCHtml footnotes={footnotes}>{cardProps.title}</JMCHtml>}
                image={cardProps.image}
                imageFocus={cardProps.imageFocus}
                imagePlacement={cardProps.imagePlacement}
                padding={cardProps.padding}
                fillImageSpace={!jnjFullBranded}
                fullBackground={jnjFullBranded ? false : cardProps.fullBackground}
                overlay={cardProps.overlay}
                variant="custom"
                loading={sectionIndex === 0 ? "eager" : "lazy"}
                isTransparent={cardProps.background_color === "transparent"}
                isClickable={jnjFullBranded}
                footer={
                    cardProps?.call_to_action?.length > 0 || !jnjFullBranded ? (
                        <CardFooter>
                            {cardProps?.call_to_action?.map(
                                (
                                    {
                                        button_collection,
                                        button,
                                    }: {
                                        button_collection?: CMSButtonCollectionProps;
                                        button?: CMSButtonProps;
                                    },
                                    index: number,
                                ) => {
                                    if (button_collection) {
                                        if (jnjFullBranded) {
                                            return button_collection?.buttons?.map((button, index) => {
                                                return buildButton(button.button, index);
                                            });
                                        } else {
                                            return (
                                                <ButtonCollection
                                                    key={`buttonCollection-${index}-${cardProps.id}`}
                                                    buttons={button_collection?.buttons?.map((button) => button.button)}
                                                    horizontal_alignment={button_collection?.horizontal_alignment}
                                                />
                                            );
                                        }
                                    } else {
                                        return buildButton(button, index);
                                    }
                                },
                            )}
                        </CardFooter>
                    ) : undefined
                }
            >
                <span>
                    <JMCHtml
                        footnotes={footnotes}
                        color={textColor}
                        placement={placement}
                        data-test-id="Card.Description.Text"
                    >
                        {cardProps.card_body}
                    </JMCHtml>
                </span>
            </GatsbyImageCard>
        </JMCCardStyleWrapper>
    );

    return card;
};
